/**
 * TEMPLATE FOR CREATING NEW DETAILS PAGES
 * 
 * To create a new details page:
 * 1. Copy this file
 * 2. Update MODEL_CONFIG block below (5 lines)
 * 3. Rename component (search/replace "Job" with your model name)
 * 4. Done!
 */

import { useLocation, useParams } from "react-router-dom";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import { canContribute } from "../hartUtils";
import { useModelDetails } from "../hartHooks";
import { Button, Box, Paper } from "@mui/material";
import JobFormFields, { validateJob, jobNullableFields } from "../components/JobFormFields";
import AssociationsSection from "../components/associations";

// ========================================
// MODEL CONFIG
// ========================================
const MODEL_CONFIG = {
  apiEndpoint: '/hart/jobs',
  idParamName: 'jobNo',
  displayName: 'Job',
  displayField: 'job_name',
  FormComponent: JobFormFields,
  dataKey: 'job',
  validateFn: validateJob,
  nullableFields: jobNullableFields,
};
// ========================================

export const JobDetailsPage = () => {
  const { projectId, [MODEL_CONFIG.idParamName]: idParam } = useParams();
  const location = useLocation();

  const {
    data,
    setData,
    editable,
    submitting,
    loading,
    error,
    validationErrors,
    handleSubmit,
    handleEditToggle,
    handleBack,
  } = useModelDetails({
    apiEndpoint: MODEL_CONFIG.apiEndpoint,
    idParam,
    initialData: location.state?.[MODEL_CONFIG.dataKey],
    validateFn: MODEL_CONFIG.validateFn,
    nullableFields: MODEL_CONFIG.nullableFields,
  });

  const canEditRole = canContribute(projectId);
  const { FormComponent, displayName } = MODEL_CONFIG;

  // Create header title with job_no and job_name
  const getHeaderTitle = () => {
    if (!data) return `${displayName}: ${idParam}`;
    const jobNo = data.job_no || idParam;
    const jobName = data.job_name || '';
    return jobName ? `${displayName}: ${jobNo} - ${jobName}` : `${displayName}: ${jobNo}`;
  };

  if (loading) return <p>Loading {displayName} details...</p>;
  if (error) return <p>Error loading {displayName}: {error}</p>;

  return (
    <>
      <PageHeader
        title={getHeaderTitle()}
        action={
          canEditRole && (
            <Button
              variant="contained"
              color={editable ? "secondary" : "primary"}
              aria-label={`Edit ${displayName}`}
              onClick={handleEditToggle}
              sx={{ '&:hover': { backgroundColor: 'secondary.main' } }}
            >
              {editable ? "Cancel Edit" : `Edit ${displayName}`}
            </Button>
          )
        }
      />
      <Paper sx={{ p: 3, mb: 3, mt: 5 }}>
        <FormComponent data={data} editable={editable} onChange={setData} errors={validationErrors} displayName={displayName} projectId={projectId} />
      </Paper>

      {/* Associations Section */}
      {!loading && data && data.job_no && (
        <AssociationsSection
          parentId={data.job_no}
          parentData={data}
          parentType="job"
          associations={['sessions', 'job_analysts', 'clients']}
          editable={editable}
          editableAssociationCallbacks={{
            job_analysts: (analysts) => setData(prev => ({ ...prev, analysts })),
            clients: (clients) => setData(prev => ({
              ...prev,
              clients,
              // Extract org_ids for the writable 'client' field expected by the serializer
              client: clients.map(c => c.org_id).filter(Boolean),
            })),
            sessions: (sessions) => setData(prev => ({
              ...prev,
              sessions,
              // Extract session_ids for the writable 'session' field expected by the serializer
              session: sessions.map(s => s.session_id).filter(Boolean),
            })),
            finances: (finances) => setData(prev => ({ ...prev, finances })),
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        {editable && canEditRole && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Submit"}
          </Button>
        )}
      </Box>
    </>
  );
};

const routeArgs = {
  element: <JobDetailsPage />,
};

export default buildRoute(routeArgs);

